import React, { useState, useEffect, useRef } from 'react';
import {
    Plus, Edit2, Trash2, Clock, User, FileText, Save, X,
    AlertCircle, CheckCircle, ChevronDown, ChevronUp,
    Clipboard, ClipboardList, Lock, FileCheck
} from 'lucide-react';
import { API_URL } from '../config/api';
import './MedicalNotes.css';

// Field definitions for each note type
const HISTORIA_CLINICA_FIELDS = [
    { key: 'motivo_consulta', label: 'Motivo de consulta', required: true },
    { key: 'antecedentes_familiares', label: 'Antecedentes familiares' },
    { key: 'antecedentes_preperinatales', label: 'Antecedentes pre y perinatales' },
    { key: 'antecedentes_neonatales', label: 'Antecedentes neonatales' },
    { key: 'crecimiento_desarrollo', label: 'Crecimiento y desarrollo' },
    { key: 'antecedentes_personales', label: 'Antecedentes personales patológicos y no patológicos' },
    { key: 'antecedentes_ginecoobstetricos', label: 'Antecedentes gineco-obstétricos' },
    { key: 'padecimiento_actual', label: 'Padecimiento actual' },
    { key: 'interrogatorio_sistemas', label: 'Interrogatorio por aparatos y sistemas' },
    { key: 'somatometria', label: 'Somatometría' },
    { key: 'exploracion_fisica', label: 'Exploración física' },
    { key: 'resultados_estudios', label: 'Resultados de estudios' },
    { key: 'diagnostico_presuntivo', label: 'Diagnóstico presuntivo' },
    { key: 'tratamiento_seguimiento', label: 'Tratamiento y seguimiento: Estudios y valoraciones solicitadas' },
    { key: 'notas_adicionales', label: 'Notas adicionales' }
];

const NOTA_EVOLUCION_FIELDS = [
    { key: 'padecimiento_actual', label: 'Padecimiento actual / Evolución', required: true },
    { key: 'crecimiento_desarrollo', label: 'Crecimiento y desarrollo' },
    { key: 'somatometria', label: 'Somatometría' },
    { key: 'exploracion_fisica', label: 'Exploración física' },
    { key: 'resultados_estudios', label: 'Resultados de estudios' },
    { key: 'diagnostico', label: 'Diagnóstico' },
    { key: 'tratamiento_seguimiento', label: 'Tratamiento y seguimiento: Estudios y valoraciones solicitadas' },
    { key: 'notas_adicionales', label: 'Notas adicionales' }
];

const MedicalNotes = ({ patientId, patientName }) => {
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingNote, setEditingNote] = useState(null);
    const [expandedNote, setExpandedNote] = useState(null);
    const [hasHistoriaClinica, setHasHistoriaClinica] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const topRef = useRef(null);

    // Form state
    const [noteType, setNoteType] = useState('nota_evolucion');
    const [formData, setFormData] = useState({});

    useEffect(() => {
        if (patientId) {
            loadNotes();
        }
    }, [patientId]);

    const loadNotes = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/patients/${patientId}/medical-notes`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const data = await response.json();
                setNotes(data.data || []);
                setHasHistoriaClinica(data.has_historia_clinica || false);
            }
        } catch (err) {
            console.error('Error loading notes:', err);
            setError('Error al cargar las notas');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({});
        setNoteType('nota_evolucion');
        setEditingNote(null);
        setShowForm(false);
        setError('');
    };

    // Open form with correct type pre-selected based on existing notes
    const handleOpenForm = () => {
        // If patient already has Historia Clínica, auto-select Nota de Evolución
        if (hasHistoriaClinica) {
            setNoteType('nota_evolucion');
        } else {
            // For new patients, default to Historia Clínica (first visit)
            setNoteType('historia_clinica');
        }
        setFormData({});
        setEditingNote(null);
        setShowForm(true);
    };

    const handleFieldChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const getCurrentFields = () => {
        return noteType === 'historia_clinica' ? HISTORIA_CLINICA_FIELDS : NOTA_EVOLUCION_FIELDS;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSubmitting(true);

        try {
            const token = localStorage.getItem('token');
            const payload = {
                note_type: noteType,
                ...formData
            };

            let url, method;
            if (editingNote) {
                url = `${API_URL}/medical-notes/${editingNote.id}`;
                method = 'PUT';
            } else {
                url = `${API_URL}/patients/${patientId}/medical-notes`;
                method = 'POST';
            }

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (response.ok) {
                const wasHistoriaClinica = noteType === 'historia_clinica' && !editingNote;

                // Reset form and reload notes
                resetForm();
                await loadNotes();

                // Show success message
                if (wasHistoriaClinica) {
                    setSuccess('Historia Clínica guardada. Ahora puede crear Notas de Evolución para visitas subsecuentes.');
                } else {
                    setSuccess(editingNote ? 'Nota actualizada exitosamente' : 'Nota de Evolución guardada exitosamente');
                }
                setTimeout(() => setSuccess(''), 4000);
            } else {
                setError(data.message || 'Error al guardar la nota');
            }
        } catch (err) {
            setError('Error de conexión');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = (note) => {
        setEditingNote(note);
        setNoteType(note.note_type);
        setFormData({
            motivo_consulta: note.motivo_consulta || '',
            antecedentes_familiares: note.antecedentes_familiares || '',
            antecedentes_preperinatales: note.antecedentes_preperinatales || '',
            antecedentes_neonatales: note.antecedentes_neonatales || '',
            crecimiento_desarrollo: note.crecimiento_desarrollo || '',
            antecedentes_personales: note.antecedentes_personales || '',
            antecedentes_ginecoobstetricos: note.antecedentes_ginecoobstetricos || '',
            padecimiento_actual: note.padecimiento_actual || '',
            interrogatorio_sistemas: note.interrogatorio_sistemas || '',
            somatometria: note.somatometria || '',
            exploracion_fisica: note.exploracion_fisica || '',
            resultados_estudios: note.resultados_estudios || '',
            diagnostico: note.diagnostico || '',
            diagnostico_presuntivo: note.diagnostico_presuntivo || '',
            tratamiento_seguimiento: note.tratamiento_seguimiento || '',
            notas_adicionales: note.notas_adicionales || '',
            informe_medico: note.informe_medico || false,
            resumen_clinico: note.resumen_clinico || false
        });
        setShowForm(true);
        setExpandedNote(null);
        if (topRef.current) {
            topRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const handleDelete = async (noteId) => {
        if (!window.confirm('¿Estás seguro de eliminar esta nota?')) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/medical-notes/${noteId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                setSuccess('Nota eliminada exitosamente');
                loadNotes();
                setTimeout(() => setSuccess(''), 3000);
            } else {
                const data = await response.json();
                setError(data.message || 'Error al eliminar');
            }
        } catch (err) {
            setError('Error de conexión');
        }
    };

    const handleSign = async (noteId) => {
        if (!window.confirm('¿Firmar esta nota? Una vez firmada no podrá ser editada.')) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/medical-notes/${noteId}/sign`, {
                method: 'PATCH',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                setSuccess('Nota firmada exitosamente');
                loadNotes();
                setTimeout(() => setSuccess(''), 3000);
            } else {
                const data = await response.json();
                setError(data.message || 'Error al firmar');
            }
        } catch (err) {
            setError('Error de conexión');
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('es-MX', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getNoteTypeInfo = (type) => {
        if (type === 'historia_clinica') {
            return { label: 'Historia Clínica', icon: <Clipboard size={20} />, className: 'type-historia' };
        }
        return { label: 'Nota de Evolución', icon: <ClipboardList size={20} />, className: 'type-evolucion' };
    };

    if (loading) {
        return <div className="medical-notes-loading">Cargando notas médicas...</div>;
    }

    return (
        <div className="note-card" ref={topRef}>
            {/* Header */}
            <div className="card-header">
                <div className="card-icon-title">
                    <div className="icon-box">
                        <FileText size={20} />
                    </div>
                    <h3 className="card-title">Notas Médicas</h3>
                </div>
                <button className="note-btn-save" onClick={handleOpenForm} style={{ padding: '0.5rem 1rem', fontSize: '0.9rem' }}>
                    <Plus size={16} />
                    {hasHistoriaClinica ? 'Nueva Evolución' : 'Historia Clínica'}
                </button>
            </div>

            {/* Messages */}
            {error && (
                <div className="alert alert-error">
                    <AlertCircle size={18} />
                    {error}
                </div>
            )}
            {success && (
                <div className="alert alert-success">
                    <CheckCircle size={18} />
                    {success}
                </div>
            )}

            {/* Form */}
            {showForm && (
                <div className="note-form-container" style={{ marginBottom: '2rem', padding: '1.5rem', border: '1px solid #e2e8f0', borderRadius: '12px', background: '#f8fafc' }}>
                    <form onSubmit={handleSubmit} className="note-form">
                        <div className="form-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                            <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#0f172a' }}>
                                {editingNote ? 'Editar Nota' : (hasHistoriaClinica ? 'Nueva Nota de Evolución' : 'Historia Clínica')}
                            </h3>
                            <button type="button" className="note-btn-cancel" onClick={resetForm} style={{ padding: '0.4rem' }}>
                                <X size={20} />
                            </button>
                        </div>

                        {/* Note Type Selector */}
                        {!editingNote && (
                            <div className="type-selector" style={{ marginBottom: '1.5rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', fontSize: '0.9rem' }}>Tipo de Nota:</label>
                                <div className="type-buttons" style={{ display: 'flex', gap: '1rem' }}>
                                    <button
                                        type="button"
                                        className={`type-btn ${noteType === 'historia_clinica' ? 'active' : ''}`}
                                        onClick={() => setNoteType('historia_clinica')}
                                        disabled={hasHistoriaClinica}
                                        style={{
                                            padding: '0.75rem 1rem',
                                            borderRadius: '8px',
                                            border: noteType === 'historia_clinica' ? '2px solid #083677' : '1px solid #cbd5e1',
                                            background: noteType === 'historia_clinica' ? '#eff6ff' : 'white',
                                            color: noteType === 'historia_clinica' ? '#083677' : '#64748b',
                                            cursor: hasHistoriaClinica ? 'not-allowed' : 'pointer',
                                            opacity: hasHistoriaClinica ? 0.6 : 1
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '600' }}>
                                            <Clipboard size={18} /> Historia Clínica
                                        </div>
                                    </button>
                                    <button
                                        type="button"
                                        className={`type-btn ${noteType === 'nota_evolucion' ? 'active' : ''}`}
                                        onClick={() => setNoteType('nota_evolucion')}
                                        style={{
                                            padding: '0.75rem 1rem',
                                            borderRadius: '8px',
                                            border: noteType === 'nota_evolucion' ? '2px solid #083677' : '1px solid #cbd5e1',
                                            background: noteType === 'nota_evolucion' ? '#eff6ff' : 'white',
                                            color: noteType === 'nota_evolucion' ? '#083677' : '#64748b',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: '600' }}>
                                            <ClipboardList size={18} /> Nota de Evolución
                                        </div>
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Special Checkboxes for all note types */}
                        <div className="special-options" style={{ 
                            display: 'flex', 
                            gap: '2rem', 
                            marginBottom: '1.5rem',
                            padding: '1rem',
                            background: 'white',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0'
                        }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: '600', color: '#0f172a' }}>
                                <input 
                                    type="checkbox" 
                                    checked={formData.informe_medico || false}
                                    onChange={(e) => handleFieldChange('informe_medico', e.target.checked)}
                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                Informe Médico
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: '600', color: '#0f172a' }}>
                                <input 
                                    type="checkbox" 
                                    checked={formData.resumen_clinico || false}
                                    onChange={(e) => handleFieldChange('resumen_clinico', e.target.checked)}
                                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                />
                                Resumen Clínico
                            </label>
                        </div>

                        {/* Dynamic Fields */}
                        <div className="medical-notes-grid" style={{ gridTemplateColumns: '1fr' }}>
                            {getCurrentFields().map(field => (
                                <div key={field.key} className="form-group">
                                    <label>
                                        {field.label}
                                        {field.required && <span style={{ color: '#ef4444' }}> *</span>}
                                    </label>
                                    <textarea
                                        className="form-control"
                                        value={formData[field.key] || ''}
                                        onChange={(e) => handleFieldChange(field.key, e.target.value)}
                                        placeholder="-"
                                        rows={3}
                                        required={field.required}
                                    />
                                </div>
                            ))}
                        </div>

                        {/* Form Actions */}
                        <div className="note-actions">
                            <button type="button" className="note-btn-cancel" onClick={resetForm}>
                                Cancelar
                            </button>
                            <button type="submit" className="note-btn-save" disabled={submitting}>
                                <Save size={18} />
                                {submitting ? 'Guardando...' : 'Guardar Nota'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Notes List */}
            <div className="notes-list" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {notes.length === 0 ? (
                    <div className="empty-state" style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>
                        <FileText size={48} style={{ margin: '0 auto 1rem', opacity: 0.5 }} />
                        <h3 style={{ fontSize: '1.2rem', fontWeight: '600', marginBottom: '0.5rem' }}>No hay notas médicas</h3>
                        <p>Crea la primera nota para este paciente</p>
                    </div>
                ) : (
                    notes.map(note => {
                        const typeInfo = getNoteTypeInfo(note.note_type);
                        const isExpanded = expandedNote === note.id;
                        const fields = note.note_type === 'historia_clinica'
                            ? HISTORIA_CLINICA_FIELDS
                            : NOTA_EVOLUCION_FIELDS;

                        return (
                            <div key={note.id} className="note-item" style={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden' }}>
                                <div
                                    className="note-header-row"
                                    onClick={() => setExpandedNote(isExpanded ? null : note.id)}
                                    style={{
                                        padding: '1rem',
                                        background: note.note_type === 'historia_clinica' ? '#f8fafc' : 'white',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '1rem'
                                    }}
                                >
                                    <div style={{
                                        padding: '0.5rem',
                                        borderRadius: '8px',
                                        background: note.note_type === 'historia_clinica' ? '#e0e7ff' : '#f1f5f9',
                                        color: note.note_type === 'historia_clinica' ? '#083677' : '#475569'
                                    }}>
                                        {typeInfo.icon}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: '600', color: '#0f172a' }}>{typeInfo.label}</div>
                                        <div style={{ fontSize: '0.85rem', color: '#64748b', display: 'flex', gap: '1rem', marginTop: '0.2rem' }}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}><Clock size={12} /> {formatDate(note.created_at)}</span>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}><User size={12} /> {note.doctor_name || 'Sin médico'}</span>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                        {note.informe_medico == 1 && (
                                            <span style={{ padding: '0.25rem 0.6rem', borderRadius: '6px', background: '#eff6ff', color: '#1d4ed8', fontSize: '0.7rem', fontWeight: '600', border: '1px solid #dbeafe' }}>Informe</span>
                                        )}
                                        {note.resumen_clinico == 1 && (
                                            <span style={{ padding: '0.25rem 0.6rem', borderRadius: '6px', background: '#f5f3ff', color: '#6d28d9', fontSize: '0.7rem', fontWeight: '600', border: '1px solid #ede9fe' }}>Resumen</span>
                                        )}
                                        {note.is_signed && <span style={{ padding: '0.25rem 0.75rem', borderRadius: '99px', background: '#dcfce7', color: '#166534', fontSize: '0.75rem', fontWeight: '600' }}>Firmada</span>}
                                    </div>
                                    {isExpanded ? <ChevronUp size={20} color="#94a3b8" /> : <ChevronDown size={20} color="#94a3b8" />}
                                </div>

                                {isExpanded && (
                                    <div className="medical-note-content" style={{ padding: '1.5rem', borderTop: '1px solid #e2e8f0' }}>
                                        <div className="medical-notes-grid" style={{ gridTemplateColumns: 'minmax(250px, 1fr)' }}>
                                            {fields.map(field => {
                                                const value = note[field.key];
                                                if (!value) return null;
                                                return (
                                                    <div key={field.key} className="form-group">
                                                        <label>{field.label}</label>
                                                        <div className="detail-value-static" style={{ height: 'auto', minHeight: '42px', alignItems: 'start', lineHeight: '1.5' }}>
                                                            {value}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {!note.is_signed && (
                                            <div className="note-actions" style={{ marginTop: '2rem' }}>
                                                <button className="note-btn-cancel" onClick={(e) => { e.stopPropagation(); handleDelete(note.id); }} style={{ color: '#ef4444', background: '#fef2f2' }}>
                                                    <Trash2 size={16} style={{ marginRight: '0.5rem' }} /> Eliminar
                                                </button>
                                                <button className="note-btn-save" onClick={(e) => { e.stopPropagation(); handleEdit(note); }} style={{ background: 'white', color: '#083677', border: '1px solid #083677' }}>
                                                    <Edit2 size={16} /> Editar
                                                </button>
                                                <button className="note-btn-save" onClick={(e) => { e.stopPropagation(); handleSign(note.id); }} style={{ background: '#083677', color: 'white' }}>
                                                    <Lock size={16} /> Firmar y Bloquear
                                                </button>

                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default MedicalNotes;
